/**
 * App theme presets (colors only, v1).
 *
 * Each preset is a flat map of semantic design tokens that get written to the
 * document root as CSS variables (`--app-*`). Components read these tokens
 * instead of hardcoded hex, so switching a preset re-skins the whole app.
 *
 * `scheme` drives Mantine's colorScheme (light/dark) so Mantine components and
 * the global normalize styles follow the theme too.
 *
 * The white-label "Design" brand color, when enabled, overrides `--app-primary`
 * on top of whichever preset is active (see config/appTheme.js).
 */

export const THEME_TOKENS = [
  "bg",
  "surface",
  "surface-2",
  "border",
  "text",
  "text-muted",
  "primary",
  "on-primary",
  "accent",
  "sidebar-bg",
  "sidebar-text",
  "sidebar-active-bg",
  "sidebar-active-text",
  "header-bg",
  "header-text",
  // Soft semantic tint backgrounds (chips, badges, banners).
  "tint-amber",
  "tint-blue",
  "tint-green",
  "tint-red",
  "tint-purple",
  // Brand wash — decorative accents that should follow the preset's primary
  // (featured cards, header bands, selection highlights), NOT the amber tint.
  "tint-primary",
  // Ink colors — the foreground paired with each tint. Light presets use deep
  // inks; dark uses bright inks so text stays readable on alpha tints.
  "ink-amber",
  "ink-blue",
  "ink-green",
  "ink-red",
  "ink-purple",
];

// Shared light-scheme tints/inks (Ogiso Light, Ocean, Evergreen). The tints are
// semantic (warning/info/success/danger/category), so they stay stable across
// light presets — only `tint-primary` follows each preset's brand color.
const LIGHT_TINTS = {
  "tint-amber": "#FFF8EB",
  "tint-blue": "#EFF6FF",
  "tint-green": "#ECFDF3",
  "tint-red": "#FEF3F2",
  "tint-purple": "#F5F3FF",
};
const LIGHT_INKS = {
  "ink-amber": "#B45309",
  "ink-blue": "#1D4ED8",
  "ink-green": "#0F766E",
  "ink-red": "#B42318",
  "ink-purple": "#6D28D9",
};

export const THEME_PRESETS = [
  {
    key: "light",
    label: "Ogiso Light",
    scheme: "light",
    tokens: {
      bg: "#F5F5F5",
      surface: "#FFFFFF",
      "surface-2": "#FAFAFA",
      border: "#E5E5E5",
      text: "#171717",
      "text-muted": "#737373",
      primary: "#FFBA45",
      "on-primary": "#171717",
      accent: "#171717",
      "sidebar-bg": "#FFFFFF",
      "sidebar-text": "#495057",
      "sidebar-active-bg": "#171717",
      "sidebar-active-text": "#FFFFFF",
      "header-bg": "#FFFFFF",
      "header-text": "#171717",
      ...LIGHT_TINTS,
      ...LIGHT_INKS,
      "tint-primary": "#FFF8EB",
    },
  },
  {
    key: "dark",
    label: "Midnight",
    scheme: "dark",
    tokens: {
      // Gray (not pure-black) page; cards a clear step lighter so the layout
      // reads with contrast. Borders kept subtle (close to surface) so they
      // never look like harsh white outlines.
      // Frame (sidebar + header) is the darkest shade; the page content sits on
      // a clearly lighter gray so it reads as distinct from the chrome.
      bg: "#23242B",
      surface: "#2C2E36",
      "surface-2": "#363842",
      border: "#3A3C46",
      text: "#F4F5F7",
      "text-muted": "#C0C2CC",
      primary: "#FFBA45",
      "on-primary": "#171717",
      accent: "#F4F5F7",
      "sidebar-bg": "#17181C",
      "sidebar-text": "#D4D4D8",
      "sidebar-active-bg": "#33353F",
      "sidebar-active-text": "#FFFFFF",
      "header-bg": "#17181C",
      "header-text": "#F4F5F7",
      // Alpha tints keep the hue as a subtle chip on dark surfaces.
      "tint-amber": "rgba(255, 186, 69, 0.16)",
      "tint-blue": "rgba(96, 165, 250, 0.16)",
      "tint-green": "rgba(52, 211, 153, 0.16)",
      "tint-red": "rgba(248, 113, 113, 0.16)",
      "tint-purple": "rgba(167, 139, 250, 0.16)",
      "tint-primary": "rgba(255, 186, 69, 0.16)",
      // Bright inks — deep light-theme inks are unreadable on alpha tints here.
      "ink-amber": "#FFBA45",
      "ink-blue": "#60A5FA",
      "ink-green": "#2DD4BF",
      "ink-red": "#FCA5A5",
      "ink-purple": "#A78BFA",
    },
  },
  {
    key: "ocean",
    label: "Ocean",
    scheme: "light",
    tokens: {
      bg: "#EEF4FB",
      surface: "#FFFFFF",
      "surface-2": "#F4F8FD",
      border: "#D6E2F0",
      text: "#0F2238",
      "text-muted": "#5A7290",
      primary: "#2563EB",
      "on-primary": "#FFFFFF",
      accent: "#0F2238",
      "sidebar-bg": "#0F2A4A",
      "sidebar-text": "#B7C9DE",
      "sidebar-active-bg": "#2563EB",
      "sidebar-active-text": "#FFFFFF",
      "header-bg": "#FFFFFF",
      "header-text": "#0F2238",
      ...LIGHT_TINTS,
      ...LIGHT_INKS,
      "tint-primary": "#EAF1FD",
    },
  },
  {
    key: "forest",
    label: "Evergreen",
    scheme: "light",
    tokens: {
      bg: "#EEF5EF",
      surface: "#FFFFFF",
      "surface-2": "#F3F8F3",
      border: "#D5E5D8",
      text: "#102219",
      "text-muted": "#5A7766",
      primary: "#15803D",
      "on-primary": "#FFFFFF",
      accent: "#102219",
      "sidebar-bg": "#10271A",
      "sidebar-text": "#BBD3C2",
      "sidebar-active-bg": "#15803D",
      "sidebar-active-text": "#FFFFFF",
      "header-bg": "#FFFFFF",
      "header-text": "#102219",
      ...LIGHT_TINTS,
      ...LIGHT_INKS,
      "tint-primary": "#EAF4EC",
    },
  },
];

export const DEFAULT_THEME_KEY = "light";

export const getPreset = (key) =>
  THEME_PRESETS.find((p) => p.key === key) || THEME_PRESETS[0];
